import type { QuizPublic } from '@repo/types/quiz';
import type { ComponentProps, ReactNode } from 'react';

import { cn } from '@repo/lib/cn';
import { QuizType } from '@repo/types/enums/quizType';
import { format } from 'date-fns';
import { AwardIcon, CalendarIcon, CheckCheckIcon, ClockIcon, FileTextIcon, TimerIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

import { formatQuizAvailable, formatQuizDeadline, formatQuizMaxAttempts, formatQuizPoints, formatQuizTime } from './quizStatFormatters';

const QUIZ_TYPE_LABEL: Record<QuizType, string> = {
  [QuizType.QUIZ]: 'Quiz',
  [QuizType.TEST]: 'Test',
  [QuizType.ASSIGNMENT]: 'Zadanie',
};

const QUIZ_TYPE_VARIANT: Record<QuizType, 'brand' | 'purple' | 'turquoise'> = {
  [QuizType.QUIZ]: 'brand',
  [QuizType.TEST]: 'purple',
  [QuizType.ASSIGNMENT]: 'turquoise',
};

interface QuizTypeBadgeProps {
  type: QuizType;
}

export function QuizTypeBadge(quizTypeBadgeProps: QuizTypeBadgeProps) {
  const { type } = quizTypeBadgeProps;
  return (
    <Badge variant={QUIZ_TYPE_VARIANT[type]} size="sm">
      {QUIZ_TYPE_LABEL[type]}
    </Badge>
  );
}

interface QuizStatProps {
  icon: ReactNode;
  label: string;
  value: string;
}

function QuizStat(quizStatProps: QuizStatProps) {
  const { icon, label, value } = quizStatProps;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-muted-foreground inline-flex items-center gap-1 text-[11px] font-medium">
        {icon}
        {label}
      </span>
      <span className="text-accent-foreground truncate text-xs font-medium">{value}</span>
    </div>
  );
}

interface AssignmentBuilderQuizCardProps extends ComponentProps<'div'> {
  quiz: QuizPublic;
  /** Bottom-left footer slot, e.g. a status badge. */
  badge?: ReactNode;
  /** Bottom-right footer slot, e.g. action buttons. */
  actions?: ReactNode;
}

export function AssignmentBuilderQuizCard(assignmentBuilderQuizCardProps: AssignmentBuilderQuizCardProps) {
  const { quiz, badge, actions, className, ...props } = assignmentBuilderQuizCardProps;
  const hasFooter = badge != null || actions != null;

  return (
    <div className={cn('border-border-primary bg-card flex flex-col overflow-hidden rounded-2xl border', className)} {...props}>
      <div className="flex items-center justify-between gap-3 p-4">
        <p className="min-w-0 truncate text-lg font-semibold">{quiz.name}</p>
        <QuizTypeBadge type={quiz.type} />
      </div>

      <Separator />

      <div className="flex flex-col gap-4 p-4">
        <div className="grid grid-cols-2 gap-3">
          <QuizStat icon={<TimerIcon className="size-3.5" />} label="Czas wykonania" value={formatQuizTime(quiz.time)} />
          <QuizStat icon={<AwardIcon className="size-3.5" />} label="Punkty" value={formatQuizPoints(quiz.points)} />
          <QuizStat icon={<CheckCheckIcon className="size-3.5" />} label="Maks. podejść" value={formatQuizMaxAttempts(quiz.maxAttempts)} />
          <QuizStat
            icon={<CalendarIcon className="size-3.5" />}
            label="Termin"
            value={formatQuizDeadline(quiz.deadline, {
              withTime: true,
              fallback: 'Brak',
            })}
          />
          <QuizStat icon={<ClockIcon className="size-3.5" />} label="Dostępny od" value={formatQuizAvailable(quiz.available)} />
          <QuizStat icon={<FileTextIcon className="size-3.5" />} label="Zaktualizowano" value={format(new Date(quiz.updatedAt), 'dd.MM.yyyy')} />
        </div>

        {hasFooter && (
          <div className="flex items-end justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">{badge}</div>
            {actions != null && <div className="flex shrink-0 flex-col items-stretch gap-2">{actions}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
